import { RollingValue } from '~/design-system/elements/RollingValue/RollingValue';
import styles from './PricingTable.module.css';
import { BillingCtaButton } from '~/design-system/components/v1_5/Settings';
import { Toggle } from '~/design-system/elements/Toggle/Toggle';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { BillingCreditSlider } from '~/design-system/components/v1_5/Settings';
import {
  ANNUAL_LABEL,
  BUY_MORE_LABEL,
  FEATURES,
  NUMBERFLOW_TIMING,
  PLAN_SUBTITLE,
  PLAN_TITLE,
  WHAT_YOU_GET_LABEL,
} from './pricingContent';

interface GeneralPlanCardProps {
  annual: boolean;
  onAnnualChange: (value: boolean) => void;
  /** Credit count for the headline (animated with NumberFlow as the slider moves). */
  headlineCredits: number;
  /** Monthly price in dollars for the headline (animated); null = the Free baseline. */
  headlinePrice: number | null;
  /** Credit count at each slider stop, low → high. */
  sliderValues: number[];
  selectedIndex: number;
  onVolumeChange: (index: number) => void;
  /** CTA label — "Upgrade" / "Downgrade" / "Cancel plan" / "Current plan", set by the parent. */
  buttonLabel: string;
  /** Contextual "what happens" rows shown under the price (icon + text). */
  explanationLines: { icon: string; text: string }[];
  onUpgrade: () => void;
  upgradeLoading?: boolean;
  upgradeDisabled?: boolean;
  /**
   * Which skin the CTA wears. NOT read from `useV15Theme` — this card's only caller is the landing's
   * pricing section, which pins its own tone (`tone="dark"` on the v2 page) independently of the
   * reader's appearance preference. Taking the app's preference here would paint a light button on
   * the dark landing for anyone browsing in Light.
   */
  theme?: 'light' | 'dark';
}

export const GeneralPlanCard = ({
  annual,
  onAnnualChange,
  headlineCredits,
  headlinePrice,
  sliderValues,
  selectedIndex,
  onVolumeChange,
  buttonLabel,
  explanationLines,
  onUpgrade,
  upgradeLoading = false,
  upgradeDisabled = false,
  theme = 'light',
}: GeneralPlanCardProps) => {
  return (
    <div className={styles.generalCard}>
      {/* Left column: plan + what you get */}
      <div className={styles.featuresCol}>
        <div className={styles.planTitleBlock}>
          <div className={styles.planTitle}>{PLAN_TITLE}</div>
          <p className={styles.planSubtitle}>{PLAN_SUBTITLE}</p>
        </div>

        <div className={styles.whatYouGet}>
          <div className={styles.whatYouGetLabel}>{WHAT_YOU_GET_LABEL}</div>
          <div className={styles.featureList}>
            {FEATURES.map((feature) => (
              <div key={feature.icon} className={styles.featureRow}>
                <DeckIcon name={feature.icon} size={20} className={styles.featureIcon} />
                <span className={styles.featureText}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vertical divider, full card height */}
      <div className={styles.cardDivider} aria-hidden />

      {/* Right column: buy controls (label + toggle, slider, price) then the CTA */}
      <div className={styles.buyCol}>
        <div className={styles.buyControls}>
          <div className={styles.buyMoreTop}>
            <div className={styles.buyMoreLabel}>{BUY_MORE_LABEL}</div>
            <div className={styles.annualRow}>
              <Toggle checked={annual} onChange={onAnnualChange} aria-label={ANNUAL_LABEL} />
              <span className={styles.annualLabel}>{ANNUAL_LABEL}</span>
            </div>
          </div>

          <div className={styles.priceSliderGroup}>
            <div className={styles.planPriceRow}>
              <span className={styles.planPriceAmount}>
                {/* One string, glyph-diffed — see `TopUpCardUI` for why there is no `suffix`. */}
                <RollingValue value={`${headlineCredits.toLocaleString('en-US')} Credits`} transition={NUMBERFLOW_TIMING} />
              </span>
              <span className={styles.planPriceRight}>
                {headlinePrice == null ? (
                  'Free / month'
                ) : (
                  <RollingValue value={`$${headlinePrice} / month`} transition={NUMBERFLOW_TIMING} />
                )}
              </span>
            </div>
            <BillingCreditSlider
              stops={sliderValues}
              value={sliderValues[selectedIndex] ?? sliderValues[0]}
              onChange={(next) => onVolumeChange(sliderValues.indexOf(next))}
              label="Credits per month"
            />
          </div>

          <ul className={styles.explainList}>
            {explanationLines.map((line) => (
              <li key={line.text} className={styles.explainRow}>
                <DeckIcon name={line.icon} size={16} className={styles.explainIcon} aria-hidden />
                <span className={styles.explainText}>{line.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/*
          * The billing page's CTA, not the legacy `Button`.
          *
          * This card IS the billing page's plan card rendered signed-out, so its buy action should be
          * the same object the signed-in one presses — `PricingTable` draws `BillingCtaButton` with
          * `key` for the action that changes what you pay, and `soft` when there is nothing to press.
          * The legacy `Button variant="upgrade"` it replaces was a different pill at a different
          * height (36 against 40) with its own hover, so the two surfaces disagreed on the one control
          * they have in common. Skin and icon rule are transcribed from `PricingTable`'s call; the
          * width is NOT — the signed-in CTA is `block` because it fills a 344px card, and this one
          * sits under a wide two-column card where a full-bleed pill reads as a banner. See
          * `.ctaInline`.
          */}
        <BillingCtaButton
          skin={upgradeDisabled ? 'soft' : 'key'}
          icon={
            upgradeDisabled
              ? undefined
              : buttonLabel === 'Downgrade' || buttonLabel === 'Cancel plan'
                ? 'downgrade'
                : 'upgrade'
          }
          className={styles.ctaInline}
          theme={theme}
          onClick={onUpgrade}
          loading={upgradeLoading}
          disabled={upgradeDisabled}
        >
          {buttonLabel}
        </BillingCtaButton>
      </div>
    </div>
  );
};
